import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import { TagTypes } from '../../../Api/Types/tagTypes';
import {
  PartyDto,
  PartySearchQueryArgs,
  SearchPartiesResponseDto,
  UpsertPartyRequest,
} from '../Types/partyTypes';
import { buildPartySearchQuery } from './partySearchQuery';
const PARTY_LIST_TAG_ID = 'LIST';

export const partiesApi = createApi({
  reducerPath: 'partiesApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [TagTypes.Party],
  endpoints: (builder) => ({
    searchParties: builder.query<SearchPartiesResponseDto, PartySearchQueryArgs>({
      query: (args) => buildPartySearchQuery(args),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((item) => ({ type: TagTypes.Party as const, id: item.id })),
              { type: TagTypes.Party, id: PARTY_LIST_TAG_ID },
            ]
          : [{ type: TagTypes.Party, id: PARTY_LIST_TAG_ID }],
    }),
    getPartyById: builder.query<PartyDto, number>({
      query: (partyId) => `/parties/${partyId}`,
      providesTags: (result, error, partyId) => [{ type: TagTypes.Party, id: partyId }],
    }),
    createParty: builder.mutation<PartyDto, UpsertPartyRequest>({
      query: (body) => ({
        url: '/parties',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagTypes.Party, id: PARTY_LIST_TAG_ID }],
    }),
    updateParty: builder.mutation<PartyDto, { id: number; data: UpsertPartyRequest }>({
      query: ({ id, data }) => ({
        url: `/parties/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Party, id },
        { type: TagTypes.Party, id: PARTY_LIST_TAG_ID },
      ],
    }),
    deleteParty: builder.mutation<void, number>({
      query: (partyId) => ({
        url: `/parties/${partyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, partyId) => [
        { type: TagTypes.Party, id: partyId },
        { type: TagTypes.Party, id: PARTY_LIST_TAG_ID },
      ],
    }),
  }),
});

export const {
  useSearchPartiesQuery,
  useGetPartyByIdQuery,
  useCreatePartyMutation,
  useUpdatePartyMutation,
  useDeletePartyMutation,
} = partiesApi;
